import {
  PropsWithChildren,
  useState,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import { noop } from 'lodash';
import Joyride, { CallBackProps, ACTIONS, Step } from 'react-joyride';

import { useContextSelector } from 'use-context-selector';

import { LocalizationContext } from '../../contexts/LocalizationContext';

import { TourTooltip } from './TourTooltip/TourTooltip';
import {
  TourStep,
  TourInstance,
  TourState,
  TourOnNextParams,
} from './Tour.types';
import { TourContext } from './Tour.context';
import { defaultOnNext } from './Tour.utils';

export type TourProps = PropsWithChildren<{
  steps: TourStep[];
  onClose?: () => void;
  onNext?: (params: TourOnNextParams) => Promise<void>;
}>;

export const Tour = forwardRef<TourInstance, TourProps>(
  (
    {
      children,
      onClose = noop,
      onNext = defaultOnNext,
      steps: initialSteps = [],
    },
    ref
  ) => {
    const [{ run, steps }, setState] = useState<TourState>({
      run: false,
      steps: [],
    });

    const translations = useContextSelector(
      LocalizationContext,
      ({ tour }) => tour
    );

    const start = useCallback(() => {
      setState(() => ({
        run: true,
        steps: initialSteps.reduce(
          (res, { id, content }): Step[] => [
            ...res,
            {
              content,
              target: `.${id}`,
              disableBeacon: true,
              locale: {
                next: translations.next,
                last: translations.last,
              },
            },
          ],
          []
        ),
      }));
    }, [initialSteps, translations]);

    const isRunning = useCallback(() => run, [run]);

    const handleOnCallback = useCallback(({ action }: CallBackProps) => {
      if (action === ACTIONS.CLOSE || action === ACTIONS.RESET) {
        setState((prev) => ({ ...prev, run: false }));
      }
    }, []);

    useImperativeHandle(ref, () => ({
      start,
      isRunning,
    }));

    return (
      <TourContext.Provider value={{ steps: initialSteps, onNext, onClose }}>
        <Joyride
          run={run}
          continuous
          steps={steps}
          disableCloseOnEsc
          disableOverlayClose
          showSkipButton={false}
          callback={handleOnCallback}
          tooltipComponent={TourTooltip}
          floaterProps={{ hideArrow: true }}
        />
        {children}
      </TourContext.Provider>
    );
  }
);
